import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { apiRequest, ErrorToast, devError } from "@ridecloud/shared";

import {
  Car,
  Users,
  BarChart3,
  DollarSign,
  Leaf,
  Menu,
  X,
  Zap,
  Shield,
  TrendingUp,
} from "lucide-react";
import { MdAndroid } from "react-icons/md";
import Footer from "../components/footer";
import About from "../components/homePage/about";
import ContactUs from "../components/homePage/contact_us";

// Function to handle smooth scrolling

const colorMap = {
  teal: "bg-teal-50",
  blue: "bg-blue-50",
  orange: "bg-orange-50",
  purple: "bg-purple-50",
};
const colorMap2 = {
  teal: "bg-teal-500",
  blue: "bg-blue-500",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
};

const ParticleField = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-green-300 rounded-full opacity-40 animate-pulse"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${2 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
};

const MorphingBlob = ({ className, delay = 0 }) => {
  return (
    <div
      className={`absolute rounded-full opacity-10 animate-pulse ${className}`}
      style={{
        animationDelay: `${delay}s`,
        animationDuration: "8s",
        background: "linear-gradient(45deg, #10b981, #34d399, #ffffff)",
        filter: "blur(60px)",
      }}
    />
  );
};

const CounterAnimation = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return (
    <span>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const StatsCard = ({
  icon: Icon,
  iconColor,
  title,
  value,
  trend,
  delay = 0,
}) => {
  return (
    <div
      className="group relative opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={`bg-white/95 backdrop-blur-xl p-8 rounded-3xl border border-${iconColor}-300 hover:scale-105 transform transition-all duration-500 hover:shadow-2xl hover:shadow-${iconColor}-500/20 hover:border-${iconColor}-200`}
      >
        <div className="flex items-center justify-between mb-6">
          <div className={`p-3 ${colorMap[iconColor]} rounded-2xl`}>
            <Icon className={`w-7 h-7 text-${iconColor}-600`} />
          </div>
          {trend && (
            <div
              className={`flex items-center text-sm font-semibold text-${iconColor}-600`}
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              {trend}
            </div>
          )}
        </div>
        <h3 className="text-sm font-semibold mb-3 text-gray-600 uppercase tracking-wide">
          {title}
        </h3>
        <p className="text-3xl font-bold text-gray-800">
          {typeof value === "number" ? <CounterAnimation end={value} /> : value}
        </p>

        <div
          className={`absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`}
        />
      </div>
    </div>
  );
};

const FloatingVehicle = ({ delay, color, size = "w-12 h-6" }) => {
  return (
    <div
      className="absolute animate-float-across opacity-30"
      style={{
        animationDelay: `${delay}s`,
        animationDuration: "20s",
        top: `${20 + Math.random() * 60}%`,
      }}
    >
      <div
        className={`${size} ${color} rounded-lg relative shadow-lg transform hover:scale-110 transition-transform`}
      >
        <div className="absolute top-0.5 left-1 w-2 h-1 bg-white/70 rounded-sm" />
        <div className="absolute top-0.5 right-1 w-2 h-1 bg-white/70 rounded-sm" />
        <div className="absolute -bottom-0.5 left-0.5 w-1 h-1 bg-gray-700 rounded-full" />
        <div className="absolute -bottom-0.5 right-0.5 w-1 h-1 bg-gray-700 rounded-full" />
      </div>
    </div>
  );
};

const FeatureCard = ({
  icon: Icon,
  iconColor,
  title,
  description,
  delay = 0,
}) => {
  return (
    <div
      className="group relative opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div
        className={`h-full flex flex-col items-center text-center bg-white/90 backdrop-blur-xl p-8 rounded-3xl border border-${iconColor}-100 hover:bg-white/95 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-${iconColor}-500/15 hover:border-${iconColor}-200`}
      >
        {/* Icon */}
        <div
          className={`w-16 h-16 ${colorMap2[iconColor]} rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 group-hover:bg-${iconColor}-100`}
        >
          <Icon className={`w-8 h-8 text-white`} />
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-800 mb-4">{title}</h3>

        {/* Description (flex-grow makes it balance height) */}
        <p className="text-gray-600 leading-relaxed text-md flex-grow">
          {description}
        </p>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl" />
      </div>
    </div>
  );
};

function App() {
  const { isNativePlatform } = Capacitor;
  const headerPadding = isNativePlatform()
    ? "pt-[env(safe-area-inset-top)]"
    : "";
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100); // small delay lets the DOM paint first
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // fixed header height
      const elementPosition = element.getBoundingClientRect().top;
      const targetPosition = elementPosition + window.pageYOffset - offset;

      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 400; // ⏳ slower scroll in ms (increase for slower)
      let start = null;

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const percent = Math.min(progress / duration, 1); // from 0 → 1

        window.scrollTo(0, startPosition + distance * percent);

        if (progress < duration) {
          window.requestAnimationFrame(step);
        }
      };

      window.requestAnimationFrame(step);
    }

    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-green-100 relative overflow-hidden pt-[env(safe-area-inset-top)]">
      {/* Particle Field */}
      <ParticleField />

      {/* Morphing Background Blobs */}
      <MorphingBlob className="w-96 h-96 -top-48 -left-48" delay={0} />
      <MorphingBlob className="w-80 h-80 top-1/4 -right-40" delay={2} />
      <MorphingBlob className="w-72 h-72 bottom-0 left-1/3" delay={4} />

      {/* Floating Vehicles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <FloatingVehicle delay={0} color="bg-green-500" />
        <FloatingVehicle delay={5} color="bg-blue-400" />
        <FloatingVehicle delay={10} color="bg-orange-300" />
        <FloatingVehicle delay={15} color="bg-red-600" size="w-16 h-8" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Header */}
        <header
          className={`fixed top-0 left-0 w-full z-50 p-6 backdrop-blur-sm ${headerPadding}`}
        >
          <nav className="flex justify-between items-center max-w-7xl mx-auto">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <img
                    src="/img/ride_cloud.png"
                    alt="icon"
                    className="w-10 h-10 sm:w-10 sm:h-10"
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                    RideCloud
                  </h2>
                  <p className="text-xs text-teal-600 font-medium">
                    Smart Carpooling
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {["Home", "Metrics", "Features", "About", "Contact us"].map(
                (item) => (
                  <Link
                    key={item}
                    className="text-gray-700 hover:text-teal-600 transition-colors duration-300 font-semibold relative group text-lg"
                    onClick={() => scrollToSection(item.toLowerCase())}
                  >
                    {item}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-600 group-hover:w-full transition-all duration-300" />
                  </Link>
                )
              )}

              {/* Download App */}
              <Link
                to="https://github.com/Gaurav-koul/Ride_Cloud/releases/download/latest/ride_cloud.apk"
                className="px-4 py-2 border-2 border-teal-500 text-teal-600 font-semibold rounded-xl 
    hover:bg-teal-500 hover:text-white transition-colors duration-300 cursor-pointer
    flex items-center gap-2"
              >
                <MdAndroid className="w-5 h-5" />
                Download App
              </Link>
            </div>
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-teal hover:bg-teal/100 rounded-xl transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </nav>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 p-6 bg-white/90 backdrop-blur-xl rounded-3xl border border-teal-100 shadow-xl space-y-4">
              {["Home", "Metrics", "Features", "About", "Contact us"].map(
                (item) => (
                  <Link
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block py-3 text-gray-700 hover:text-teal-600 font-semibold text-lg"
                  >
                    {item}
                  </Link>
                )
              )}
              <Link
                to="https://github.com/Gaurav-koul/Ride_Cloud/releases/download/latest/ride_cloud.apk"
                className="flex items-center justify-center gap-2 py-3 px-4 border-2 border-teal-500 
             text-teal-600 font-semibold rounded-xl hover:bg-teal-500 hover:text-white 
             transition-colors duration-300 cursor-pointer"
              >
                <MdAndroid className="w-5 h-5" />
                <span>Download App</span>
              </Link>
            </div>
          )}
        </header>
        {/* Hero Section */}
        <main
          id="home"
          className="flex flex-col justify-center px-6 py-12 mt-24 relative pt-20"
        >
          {/* Floating Vehicles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <FloatingVehicle delay={0} color="bg-green-500" />
            <FloatingVehicle delay={5} color="bg-blue-400" />
            <FloatingVehicle delay={10} color="bg-orange-300" />
            <FloatingVehicle delay={15} color="bg-red-600" size="w-16 h-8" />
          </div>
          <div className="max-w-7xl mx-auto w-full text-center relative z-10">
            {/* Main Title */}
            <div className="mb-12 opacity-0 animate-fade-in-up text-center">
              <h2 className="text-5xl md:text-6xl lg:text-8xl font-bold mb-4 leading-snug">
                <span className="bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Welcome to
                </span>
                <span className="inline-block w-0"></span> {/* gap between */}
                <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent animate-gradient-x">
                  RideCloud
                </span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl md:max-w-xl lg:max-w-5xl mx-auto leading-relaxed font-medium">
                Experience the future of transportation management with our
                cutting-edge admin platform
              </p>
            </div>

            {/* Action Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-8 justify-center mb-24 opacity-0 animate-fade-in-up"
              style={{ animationDelay: "200ms" }}
            >
              <button
                onClick={() => navigate("/login")}
                className="group relative flex justify-center items-center px-6 sm:px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-3xl shadow-2xl hover:shadow-teal-500/30 transform hover:scale-105 transition-all duration-300 overflow-hidden text-lg"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative z-10 flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  User Login
                </span>
                <div className="absolute inset-0 -top-2 -left-2 bg-white/20 transform rotate-12 group-hover:rotate-45 transition-transform duration-500 opacity-0 group-hover:opacity-100" />
              </button>

              <button
                onClick={() => navigate("/signup")}
                className="group relative flex justify-center items-center px-6 sm:px-10 py-5 bg-white border-2 border-teal-500 text-teal-600 font-bold rounded-3xl shadow-2xl hover:shadow-teal-500/20 transform hover:scale-105 transition-all duration-300 overflow-hidden text-lg hover:bg-green-50"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Create Account
                </span>
                <div className="absolute inset-0 -top-2 -left-2 bg-green-500/10 transform rotate-12 group-hover:rotate-45 transition-transform duration-500 opacity-0 group-hover:opacity-100" />
              </button>
            </div>

            {/* Stats Grid */}
            <div
              id="metrics"
              className="pt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
            >
              <StatsCard
                icon={Users}
                iconColor="blue"
                title="Active Users"
                value={12847}
                trend="+12%"
                delay={400}
              />
              <StatsCard
                icon={Car}
                iconColor="teal"
                title="Daily Rides"
                value={3429}
                trend="+8%"
                delay={500}
              />
              <StatsCard
                icon={DollarSign}
                iconColor="orange"
                title="Revenue"
                value="₹89,432"
                trend="+15%"
                delay={600}
              />
              <StatsCard
                icon={Leaf}
                iconColor="purple"
                title="CO₂ Saved"
                value="2.4 tons"
                trend="+22%"
                delay={700}
              />
            </div>

            {/* Feature Cards */}
            <div
              id="features"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
            >
              <FeatureCard
                icon={Car}
                iconColor="teal"
                title="Your pick of rides at low prices"
                description="No matter where you're going, by bus or carpool, find the perfect ride from our wide range of destinations and routes at low prices."
                delay={800}
              />
              <FeatureCard
                icon={Users}
                iconColor="blue"
                title="Trust who you travel with"
                description="We take the time to get to know each of our members and bus partners. We check reviews, profiles and IDs, so you know who you're travelling with and can book your ride at ease on our secure platform."
                delay={900}
              />
              <FeatureCard
                icon={Zap}
                iconColor="orange"
                title="Scroll, click, tap and go!"
                description="Booking a ride has never been easier! Thanks to our simple app powered by great technology, you can book a ride close to you in just minutes."
                delay={1000}
              />
            </div>
          </div>
        </main>
        {/* About */}
        <About />
        {/* Contact */}
        <ContactUs />
        {/* Footer */}
        <Footer />
      </div>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float-across {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes gradient-x {
          0%,
          100% {
            background-size: 200% 200%;
            background-position: left center;
          }
          50% {
            background-size: 200% 200%;
            background-position: right center;
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .animate-float-across {
          animation: float-across 20s linear infinite;
        }

        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </div>
  );
}

export default App;
